import _ from "lodash";
import moment from "moment";
import naturalSortFn from "common/filters/natural_sort.pojo";

export default function (
    $rootScope,
    $sbCurrentProject,
    $q,
    $filter,
    $sbProcessTemplatesApi,
    $sbPerformanceApi
) {
    "ngInject";
    const naturalSort = $filter("naturalSort");

    // this cache is used to cache information as long as no page change occurred
    //
    var cache = null;

    $rootScope.$on("sb.global.logout", clearCache);

    // API
    //
    return {
        get: get,
        getTemplates: getTemplates,
        clearCache: clearCache,
        asMap: asMap,
        getTemplateDetails: getTemplateDetails,
        getListOfAllActivitiesOfAllTemplates:
            getListOfAllActivitiesOfAllTemplates,
        parseTemplates: parseTemplates,
    };

    /**
     * Fetch all the content from the given templateId template
     *
     * @param templateId
     */
    function getTemplateDetails(templateId) {
        const projectId = $sbCurrentProject.pick("id");
        return $sbProcessTemplatesApi
            .getLatestRevision(projectId, templateId)
            .then(_mapToOldRevisionInterface);
    }

    function getTemplates() {
        if (_hasCachedState()) {
            return $q.resolve(cache);
        } else {
            return fetchTemplatesOfCurrentProject()
                .then(function (templates) {
                    return templates.map(_postProcess);
                })
                .then(_addToCache);
        }
    }

    function asMap() {
        return getTemplates().then(
            (list) => new Map(list.map((item) => [item.ID, item]))
        );
    }

    function fetchTemplatesOfCurrentProject() {
        const projectId = $sbCurrentProject.pick("id");
        const name = $sbCurrentProject.pick("name");

        return $sbProcessTemplatesApi
            .getCollection(projectId)
            .then(({ process_templates }) => {
                const templates = process_templates.map((processTemplate) =>
                    _parseProcessTemplateResponseToOldInterface(
                        { projectId, projectName: name },
                        processTemplate
                    )
                );
                return naturalSort(templates, "NAME");
            });
    }

    /**
     * Get a template by templateId
     * id {String}
     *
     * @return {Promise}
     */
    function get(id) {
        if (!id) {
            return $q.resolve(undefined);
        }

        return getTemplates().then(() => {
            return _fromCache(id);
        });
    }

    function _fetchStageSuggestionsOfProjectBy(projectId) {
        return $sbPerformanceApi.getCollection(projectId, {
            date_range: {
                start_date: Number.NEGATIVE_INFINITY.toString(),
                end_date: moment().startOf("day").format(),
            },
            requests: [
                {
                    metric: {
                        expression: "sb::count-activity",
                    },
                    dimension: {
                        name: "sb::activity-template-id",
                    },
                    pivot: {
                        metric: {
                            expression: "sb::count-activity",
                        },
                    },
                },
            ],
        });
    }

    function _filterForActivities(suggestions) {
        return suggestions.filter(function (suggestion) {
            return suggestion.stage.category === "ACTIVITY";
        });
    }

    function _mapToAggregatedItem(suggestions) {
        return suggestions.map(function (suggestion) {
            // map to a nice data representation
            return {
                rootId: suggestion.templateId,
                rootName: suggestion.templateName,
                name: suggestion.stage.name,
                id: suggestion.stage.id,
                topologicalIndex: suggestion.stage.topologicalIndex,
            };
        });
    }

    /**
     * Sort activities so they match the ordering in the workflow template editor:
     * group activities by their activity group hierarchy first, then by topological index
     * within each group. Groups are ordered by the minimum topological index of their
     * contained activities, which matches how groups are ordered in the template editor.
     *
     * Activity names use the format "Group  |  SubGroup  |  Activity Name" where "  |  "
     * separates each level of the hierarchy.
     */
    function _sortByGroupAndTopologicalIndex(suggestions) {
        var SEPARATOR = "  |  ";

        // Build the lookup key for a group prefix at the given depth level within a template.
        // 'nameParts' is the split activity name array; 'depth' is the number of leading
        // segments to include (1-based, so depth=1 is the first group, depth=2 is the
        // first two group levels, etc.).
        function buildPrefixKey(rootId, nameParts, depth) {
            return rootId + "/" + nameParts.slice(0, depth).join(SEPARATOR);
        }

        // Build a map of group prefix paths → minimum topological index.
        // A group's sort position equals the lowest topological index among all its
        // contained activities (matching the template editor's topological ordering
        // for groups). The loop stops before parts.length so the activity name
        // (last segment) is never treated as a group prefix.
        var groupPrefixMinTopoIndex = {};
        suggestions.forEach(function (suggestion) {
            var parts = suggestion.name.split(SEPARATOR);
            for (var i = 1; i < parts.length; i++) {
                var prefix = buildPrefixKey(suggestion.rootId, parts, i);
                var currentMin = groupPrefixMinTopoIndex[prefix];
                if (
                    currentMin === undefined ||
                    suggestion.topologicalIndex < currentMin
                ) {
                    groupPrefixMinTopoIndex[prefix] =
                        suggestion.topologicalIndex;
                }
            }
        });

        // Compute a hierarchical sort key: [groupLevel1MinTopo, ..., activityTopoIndex].
        function getSortKey(suggestion) {
            var parts = suggestion.name.split(SEPARATOR);
            var key = [];
            for (var i = 1; i < parts.length; i++) {
                var prefix = buildPrefixKey(suggestion.rootId, parts, i);
                key.push(groupPrefixMinTopoIndex[prefix] || 0);
            }
            key.push(suggestion.topologicalIndex);
            return key;
        }

        return suggestions.sort(function (a, b) {
            var rootNameCompare = naturalSortFn(a.rootName, b.rootName);
            if (rootNameCompare !== 0) {
                return rootNameCompare;
            }

            var keyA = getSortKey(a);
            var keyB = getSortKey(b);
            var partsA = a.name.split(SEPARATOR);
            var partsB = b.name.split(SEPARATOR);
            var groupLevels = Math.max(partsA.length, partsB.length) - 1;

            for (var i = 0; i < groupLevels; i++) {
                var diff = (keyA[i] || 0) - (keyB[i] || 0);
                if (diff !== 0) {
                    return diff;
                }

                if (partsA[i] && partsB[i]) {
                    var groupNameCompare = naturalSortFn(partsA[i], partsB[i]);
                    if (groupNameCompare !== 0) {
                        return groupNameCompare;
                    }
                }
            }

            var activityDiff =
                (keyA[groupLevels] || 0) - (keyB[groupLevels] || 0);
            if (activityDiff !== 0) {
                return activityDiff;
            }

            return naturalSortFn(a.name, b.name);
        });
    }

    function getListOfAllActivitiesOfAllTemplates(projectId) {
        return _fetchStageSuggestionsOfProjectBy(projectId)
            .then(_filterForActivities)
            .then(_mapToAggregatedItem)
            .then(_sortByGroupAndTopologicalIndex);
    }

    function parseTemplates(templates) {
        return templates.map(function (template) {
            return {
                activitiesCount: template.ACTIVITIES_COUNT,
                category: template.CATEGORY,
                code: template.CODE,
                content: template.CONTENT,
                creationDate: template.CREATION_DATE,
                creatorDbName: template.CREATOR_DB_NAME,
                creatorDisplayName: template.CREATOR_DISPLAY_NAME,
                creatorInitials: template.CREATOR_INITIALS,
                desc: template.DESC,
                id: template.ID,
                language: template.LANGUAGE,
                lastChangeDate: template.LAST_CHANGE_DATE,
                name: template.NAME,
                project: template.PROJECT,
                projectId: template.PROJECT_ID,
                status: template.STATUS,
                templateId: template.TEMPLATE_ID,
                topologicalIndex: template.TOPOLOGICAL_INDEX,
                usedInComponents: template.USED_IN_COMPONENTS,
            };
        });
    }

    function _postProcess(template) {
        template.ACTIVITIES_COUNT = template.ACTIVITIES_COUNT || 0;
        return template;
    }

    function clearCache() {
        cache = null;
    }

    function _addToCache(template) {
        if (_.isArray(template)) {
            cache = [].concat(template);
        } else {
            if (_hasCachedState()) {
                cache.push(template);
            } else {
                cache = [template];
            }
        }
        return template;
    }

    function _fromCache(id) {
        if (!_hasCachedState()) {
            return undefined;
        }

        var index = _.findIndex(cache, function (o) {
            return o.ID === id;
        });
        if (index > -1) {
            return cache[index];
        } else {
            return undefined;
        }
    }

    function _hasCachedState() {
        return cache !== null;
    }

    function _parseProcessTemplateResponseToOldInterface(
        { projectId, projectName },
        processTemplate
    ) {
        if (!processTemplate) {
            return;
        }
        return {
            PROJECT_ID: projectId,
            PROJECT_NAME: projectName,
            ID: processTemplate.id,
            CATEGORY: processTemplate.category.toUpperCase(),
            CODE: processTemplate.code,
            DESC: processTemplate.description,
            LANGUAGE: processTemplate.language.toUpperCase(),
            NAME: processTemplate.name,
            ACTIVITIES_COUNT: processTemplate.activities_count,
            USED_IN_COMPONENTS: processTemplate.used_in_components,
            CREATION_DATE: processTemplate.created.at,
            CREATOR_DB_NAME: processTemplate.created.by.id,
            CREATOR_DISPLAY_NAME: processTemplate.created.by.name,
            CREATOR_INITIALS: processTemplate.created.by.initials,
            REVISION: processTemplate.revision,
            HAS_CHECKLIST_TEMPLATES_V2:
                processTemplate.has_checklist_templates_v2 === true,
        };
    }

    function _mapToOldRevisionInterface(processTemplate) {
        processTemplate.components = processTemplate.components.map(
            (component) => {
                const tmp = {
                    id: component.id,
                    parentId: component.parent_id || null,
                    name: component.name,
                    desc: component.desc || null,
                    code: component.code,
                    template: component.template_id || null,
                    category: component.category.toUpperCase(),
                    duration: component.duration,
                    unit: component.unit,
                    assignedTeamId: component.assigned_team_id || null,
                    color: component.color || null,
                    labour: component.labour || null,
                    topologicalIndex:
                        component.topological_index === undefined
                            ? null
                            : component.topological_index,
                    checklistId: component.checklist_id || null,
                    confirmationTeamId: component.confirmation_team_id || null,
                    reviewTeamIds: component.review_team_ids || null,
                };
                if (component.state) {
                    tmp.statename = component.state.name || null;
                    tmp.statedesc = component.state.desc || null;
                    tmp.statecode = component.state.code || null;
                }
                return tmp;
            }
        );
        return processTemplate;
    }
}
